import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Box, IconButton } from "@mui/material";
import dayjs from "dayjs";
import { MRT_ColumnFiltersState } from "material-react-table";
import React, { useEffect, useRef, useState } from "react";
import { formatDate } from "src/Utils/DateUtils";
import SingleFilter from "./SingleFilter";

export const dateRangeFilters = [
  "grnDate",
  "initiationDate",
  "earlyPayDate",
  "poDate",
  "invoiceAccountDate",
  "otherAdjustmentDate",
  "dueDate",
  "newDueDate",
  "lastPaymentDate",
  "invoiceDate",
  "invoiceAccountingDate",
];
export const numberRangeFilters = [
  "amountDisbursed",
  "discountAmount",
  "grnAmount",
  "incomeAccruedToSwiggy",
  "invoiceAmount",
  "poAmount",
  "tds",
  "purchaseReturnAmount",
  "brandDiscount",
  "debitAmount",
  "adjustments",
  "netPayableAmount",
  "paymentAmount",
  "outstandingAmount",
  "creditPeriod",
  "holdOffAmount",
  "finalAdjustments",
  "outstandingAmountNakad",
  "startingCB",
  "effectiveCB",
  "grossPaymentAmount",
  "invoiceAmount",
  "balanceAmountToBePaidOnDueDate",
  "cnAgeing",
  "incomeAccruedToSwiggy",
];
const multiSelectFilters = [
  "earlyPaymentStatus",
  "dnAdjusted",
  "anchorCnReceived",
  "nakadCnReceived",
  "cnAgeingStatus",
  "invoicePostedStatus",
  "invoiceReconciledStatus",
  "overDueStatus",
  "paymentStatus",
  "discountingStatus",
  "earlyPayPass",
  "cnStatus",
];

export const mergeMainAndTableFilters = (columnFilters, appliedFilters) => {
  const mergedFilters = Object.fromEntries(
    Object.entries(appliedFilters).filter(([_, value]) => {
      return (value as any).length > 0 && (value as any).some((v) => v !== undefined && v !== null && v !== "");
    })
  );

  // Iterate over columnFilters and merge values
  columnFilters.forEach((filter) => {
    const { id, value } = filter;

    const isDateRangeFilter = dateRangeFilters.includes(id);
    const isNumberRangeFilter = numberRangeFilters.includes(id);

    if (isDateRangeFilter) {
      const startDate = value[0] ? formatDate(dayjs(value[0]).toDate()) : null;
      const endDate = value[1] ? formatDate(dayjs(value[1]).toDate()) : null;
      if (startDate === null && endDate === null) return;

      mergedFilters[id] = [startDate, endDate];
    } else if (isNumberRangeFilter) {
      const min = value[0] ? value[0] : null;
      const max = value[1] ? value[1] : null;
      if (min === null && max === null) return;
      mergedFilters[id] = [min, max];
    } else if (multiSelectFilters.includes(id) || (value as any).length === 0) {
      mergedFilters[id] = value;
    } else {
      return;
    }
  });

  return Object.entries(mergedFilters);
};

const FiltersBars = ({
  columnFilters,
  setColumnFilters,
  appliedFilters,
  setAppliedFilters,
}: {
  appliedFilters: any;
  setAppliedFilters: React.Dispatch<React.SetStateAction<any>>;
  columnFilters: MRT_ColumnFiltersState;
  setColumnFilters: React.Dispatch<React.SetStateAction<MRT_ColumnFiltersState>>;
}) => {
  const mergedFiltersArray = mergeMainAndTableFilters(columnFilters, appliedFilters);
  if (mergedFiltersArray.length === 0) return null;

  const containerRef = useRef<HTMLDivElement>(null);
  const [disableLeftButton, setDisableLeftButton] = useState(true);
  const [disableRightButton, setDisableRightButton] = useState(true);

  const updateButtonsState = () => {
    if (containerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = containerRef.current;
      setDisableLeftButton(scrollLeft <= 0);
      setDisableRightButton(scrollLeft + clientWidth >= scrollWidth);
    }
  };

  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      updateButtonsState();
      container.addEventListener("scroll", updateButtonsState);
    }
    window.addEventListener("resize", updateButtonsState);
    return () => {
      if (container) {
        container.removeEventListener("scroll", updateButtonsState);
      }
      window.removeEventListener("resize", updateButtonsState);
    };
  }, []);

  const scroll = (direction: "left" | "right") => {
    if (containerRef.current) {
      const { clientWidth } = containerRef.current;
      const scrollAmount = direction === "left" ? -clientWidth / 1.2 : clientWidth / 1.2;
      containerRef.current.scrollBy({ left: scrollAmount, behavior: "smooth" });
    }
  };

  return (
    <Box sx={{ display: "flex", mt: 2 }}>
      <IconButton onClick={() => scroll("left")} disabled={disableLeftButton}>
        <ChevronLeftIcon />
      </IconButton>
      <Box
        sx={{
          display: "flex",
          overflowX: "auto",
          width: "95%",
          gap: 2,
          scrollBehavior: "smooth",
          scrollbarWidth: "none",
        }}
        ref={containerRef}
      >
        {mergedFiltersArray.map(([key, filteredValues]) => (
          <SingleFilter
            key={key}
            filterName={key}
            filteredValues={filteredValues}
            setColumnFilters={setColumnFilters}
            setAppliedFilters={setAppliedFilters}
          />
        ))}
      </Box>
      <IconButton onClick={() => scroll("right")} disabled={disableRightButton}>
        <ChevronRightIcon />
      </IconButton>
    </Box>
  );
};

export default FiltersBars;
